// src/components/CapturePhotoUploadButton.jsx
import React, { useRef, useState, useCallback } from 'react';
import { Button, CircularProgress, Snackbar, Alert } from '@mui/material';
import PropTypes from 'prop-types';
import validate from 'repo/validate';

async function updateRomaneioStatus(id, token) {
  const res = await fetch(
    `https://api.lc-transportes.com/api/romaneios/update/codigo/${id}`,
    {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        dataFinal: new Date().toISOString(),
        status: 'finalizado',
        motorista: localStorage.getItem('id'),
      }),
    }
  );
  if (!res.ok) throw new Error(`Status ${res.status}`);
}

async function uploadRomaneioImage(id, base64, token) {
  const res = await fetch(
    `https://api.lc-transportes.com/api/romaneios/imageUpload/${id}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ base64Image: base64 }),
    }
  );
  if (!res.ok) throw new Error(`Status ${res.status}`);
}

export default function CapturePhotoUploadButton({ id, onSuccess }) {
  const inputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const showMessage = useCallback((message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const handleButtonClick = () => {
    inputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // lê como base64
    const reader = new FileReader();
    reader.onloadend = async () => {
      const base64 = reader.result.split(',')[1];
      const token = localStorage.getItem('accessToken');

      // valida token
      if (!token || !validate(token)) {
        return showMessage('Token inválido ou expirado.', 'warning');
      }

      setLoading(true);
      try {
        // atualiza status
        await updateRomaneioStatus(id, token);
        // envia imagem
        await uploadRomaneioImage(id, base64, token);

        showMessage('Upload realizado com sucesso!', 'success');
       
        onSuccess?.(); // callback opcional
        window.location.href = '/motorista';
      } catch (err) {
        console.error(err);
        showMessage(`Erro no upload: ${err.message}`, 'error');
      } finally {
        setLoading(false);
        // limpa input pra poder reenviar a mesma foto depois, se precisar
        if (inputRef.current) inputRef.current.value = '';
      }
    };
    reader.readAsDataURL(file);
  };

  return (
    <>
      <input
        type="file"
        accept="image/*"
        capture="environment"
        ref={inputRef}
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />

      <Button
        variant="contained"
        onClick={handleButtonClick}
        disabled={loading}
        startIcon={loading ? <CircularProgress size={24} /> : <i className="fa fa-camera" />}
      >
        Finalizar Romaneio
      </Button>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}

CapturePhotoUploadButton.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onSuccess: PropTypes.func, // opcional: callback quando tudo der certo
};
